import React, { useEffect, useState, useRef, useLayoutEffect } from "react";

import * as am5 from "@amcharts/amcharts5";
import PropTypes from "prop-types";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {
  initXAxis,
} from "./amcharts.jsx";
import { HANDLING_PRESS_LOAD_TAGS, HANDLING_PRESS_UNLOAD_TAGS, TRANSPORT_PRESS_TAGS } from "../../constants/cargo/charts";
import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType.js";
import { ColorPicker } from "./colorPicker.jsx";
import { PRESS_TABS } from "../../constants/cargo/constants.js";
import { DEFAULT_LINE_COLORS } from "../../constants/trendGraph/linesDefaultColors.js";
import { useSelector } from "react-redux";
import { selectedTimezone } from "../../model/slice/timezoneSlice.js";
import { TIMEZONE } from "../../constants/timezone.js";
import { CHART } from "../../constants/trendGraph/chartType.js";
import { handleTooltipLock, handleTooltipUnlocking } from "./chartHelper.js";
import { addLegendFeatures, handleScrollBarByKeyboard, initCursor, initLegend, initScrollbarX, initScrollbarY, initYAxis } from "./chartCommon.js";

let isLocked = false;

export const LineChart = (props) => {
  const { vesselId, tagList, data, currentTab, isOneSec } = props;
  const vesselName = props.vesselId;
  const chartId = `linechart-${currentTab}`;
  const getParamList = () => {
    switch(currentTab){
      case PRESS_TABS.TRANSPORT:
        return TRANSPORT_PRESS_TAGS;
      case PRESS_TABS.LOADING:
        return HANDLING_PRESS_LOAD_TAGS;
      case PRESS_TABS.UNLOADING:
        return HANDLING_PRESS_UNLOAD_TAGS;
      default:
        return [];
    }
  }
  const paramList = getParamList();
  //for Color select trick
  const [displayColorPickerFlag, setDisplayColorPickerFlag] = useState(false);
  const [displayColorPicker, setDisplayColorPicker] = useState({
    flag: "",
    position: 0,
  });
  const [dataKeyName, setDataKeyName] = useState("");
  const [displayDataKeyName, setDisplayDataKeyName] = useState('');
  const [isDigital, setIsDigital] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState({ x: 0, y: 0 });

  const lineChartRef = useRef(null);
  const rootRef = useRef(null);
  const legendRef = useRef(null);
  const registeredHideLineList = JSON.parse(sessionStorage.getItem(`cargo-pressure-${vesselId}`));
  const [hideLineList, updateHideLineList] = useState(registeredHideLineList ?? []);
  const [operationCheck, setOperationCheck] = useState(false);
  const timezone = useSelector(selectedTimezone);
  
  useEffect(() => {
    sessionStorage.setItem(`cargo-pressure-${vesselId}`, JSON.stringify(hideLineList));
  }, [hideLineList]);

  useLayoutEffect(() => {
    am5.addLicense(process.env.AMCHARTS_LICENSE);
    if (!data?.length) return;
    const groot = am5.Root.new(chartId, {
      useSafeResolution: false,
      tooltipContainerBounds: {
        top: 50,
        right: 100,
        bottom: 50,
        left: 100
      },
    });
    groot.utc = timezone === TIMEZONE.UTC;
    groot.setThemes([am5themes_Animated.new(groot)]);
    const xyChart = groot.container.children.push(
      am5xy.XYChart.new(groot, {
        wheelY: "none",
        pinchZoomX: "none",
        pinchZoomY: "none",
        maxTooltipDistance: -1,
        layout: groot.verticalLayout,
        panX: false,
        panY: false,
      })
    );
    const dateAxis = initXAxis(groot, xyChart, isOneSec, undefined, () => { });
    const valueAxis = initYAxis(groot, xyChart, false, true);
    const scrollbarX = initScrollbarX(xyChart, groot, false, isLocked, handleUnlock);
    const scrollbarY = initScrollbarY(xyChart, groot, false, isLocked, handleUnlock);

    const listener =  { scrollbarX, scrollbarY, handleEvent: handleScrollBarByKeyboard };
    document.addEventListener("keydown", listener, false);

    initLineSeries(groot, xyChart, paramList, data, dateAxis, valueAxis);
    initCursor(xyChart, groot, dateAxis);
    const legend = initLegend(
      [xyChart],
      xyChart,
      groot,
      setDataKeyName,
      setDisplayDataKeyName,
      setIsDigital,
      setColorPickerPos,
      setDisplayColorPicker,
      setDisplayColorPickerFlag,
      GRAPH_TYPE.PRESSURE,
    );
    legend.allChildren().forEach((v) => {
      tagList.forEach((item) => {
        if(v.dataItem?.get('name') === item.tag) {
          v.set('fill', am5.color('#000000'))
          v.set("tooltipText", item.channelName);
        }
      })
    })

    xyChart.plotContainer.events.on("click", function (ev) {
      if (isLocked) {
        handleUnlock();
      } else {
        handleLock();
      }
    });
    //Click stop trick : END

    //click zoom out button also
    xyChart.zoomOutButton.events.on("click", (e) => {
      if (isLocked) {
        handleUnlock();
      }
    });
    xyChart.appear(1000, 100);
    legendRef.current = legend;
    lineChartRef.current = xyChart;
    rootRef.current = groot;
    return () => {
      document.removeEventListener("keydown", listener);
      handleUnlock();
      groot.dispose();
    };
  }, [currentTab, JSON.stringify(data), timezone]);

  useEffect(() => {
    //for User change line color trick
    if (displayColorPicker.flag == "" || displayColorPicker.position == 0) {
      // Do nothing
    } else {
      lineChartRef.current.series.each(function (item, index) {
        if (displayColorPicker.flag == item.get('key')) {
          //color change trick
          item.set("fill", am5.color(displayColorPicker.position));
          item.set("stroke", am5.color(displayColorPicker.position));
          legendRef.current.data.setAll(lineChartRef.current.series.values);
        }
      });
      legendRef.current.data.setAll(lineChartRef.current.series.values);
      addLegendFeatures(rootRef.current, lineChartRef.current, legendRef.current);
    }
  }, [displayColorPicker]);

  const initLineSeries = (root, chart, paramList, shownData, dateAxis, valueAxis) => {
    paramList.forEach((value, index) => {
      const color = sessionStorage.getItem(`cargo-pressure-${vesselId}-${value.tag}`)
          ? sessionStorage.getItem(`cargo-pressure-${vesselId}-${value.tag}`)
          : DEFAULT_LINE_COLORS[index];
      const notVisibleFlag = hideLineList.includes(value.tag);
      const valueSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: value.tag,
          key: value.tag,
          valueXField: "dateTime",
          valueYField: value.tag,
          xAxis: dateAxis,
          yAxis: valueAxis,
          calculateAggregates: true,
          visible: !notVisibleFlag,
          fill: am5.color(color),
          stroke: am5.color(color),
          legendLabelText: value.tag,
          legendValueText: "{valueY}",
          legendRangeValueText: "  "
        })
      );
      if (notVisibleFlag) {
        valueSeries.hide();
      }
      valueSeries.on("visible", (visible, target) => {
        const targetName = target.get("name");
        if (!operationCheck) {
          setOperationCheck(true);
        }
        if (visible) {
          updateHideLineList((prev) => prev.filter((d) => d !== targetName));
        } else {
          updateHideLineList((prev) => prev.concat([targetName]));
        }
      });
      valueSeries.data.setAll(shownData);
    });
  };

  const handleLock = () => {
    isLocked = true;
    handleTooltipLock(CHART.ANALOG, [lineChartRef.current]);
  }

  const handleUnlock = () => {
    isLocked = false;
    handleTooltipUnlocking([lineChartRef.current]);
  }

  return (
    <>
      <div id={chartId} style={{ width: "100%", height: "100%" }} />
      <div
        className="colorPickerContainer"
        style={{
          width: "fit-content",
          visibility: displayColorPickerFlag ? "visible" : "hidden",
          position: "relative",
          left: colorPickerPos.x,
          bottom: colorPickerPos.y,
        }}
      >
        <div style={{ left: `10px` }}>
          <ColorPicker
            vesselId={vesselId}
            setDisplayColorPicker={setDisplayColorPicker}
            dataKey={dataKeyName}
            displayDataKeyName={displayDataKeyName}
            graphType={GRAPH_TYPE.PRESSURE}
            setOnClose={setDisplayColorPickerFlag}
            vesselName={vesselName}
            isCargoPressure={true}
            isDigital={isDigital}
          />
        </div>
      </div>
    </>
  );
};

LineChart.propTypes = {
  data: PropTypes.array,
  tagList: PropTypes.array,
  currentTab: PropTypes.string,
  vesselId: PropTypes.string,
  isOneSec: PropTypes.bool
};
